import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { SERVICES } from "../data/services";
import CTABand from "../components/CTABand";
import { useSEO } from "../lib/seo";

export default function Services() {
  useSEO({
    title: "Healthcare Consulting Services · SNF & LTC · Missouri",
    description:
      "Eight strategic consulting engagements for skilled nursing and long-term care operators in Missouri: Medicaid pending recovery, survey readiness, operational stabilization, litigation support, social services audits, staff training, and process improvement.",
    path: "/services",
  });
  return (
    <div data-testid="services-page" className="bg-ivory">
      {/* HERO */}
      <section className="section-pad pt-32 bg-paper">
        <div className="container-luxe max-w-5xl">
          <div className="overline mb-5">Services</div>
          <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl text-navy leading-[0.95] mb-8">
            Eight engagements.<br />
            <span className="italic text-teal">One outcome.</span>
          </h1>
          <p className="font-sans text-lg sm:text-xl text-navy/75 max-w-3xl leading-relaxed">
            AthenaCrest Defense provides strategic, operationally grounded
            consulting for skilled nursing facilities, long-term care
            organizations, healthcare startups, and the attorneys who defend
            them. Every engagement begins with a confidential review of the
            facility's most expensive operational risks.
          </p>
        </div>
      </section>

      {/* SERVICES GRID */}
      <section className="section-pad" data-testid="services-grid">
        <div className="container-luxe">
          <div className="space-y-20">
            {SERVICES.map((s, idx) => (
              <article
                key={s.slug}
                className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-start group"
                data-testid={`service-${s.slug}`}
              >
                <div className="lg:col-span-4">
                  <div className="font-serif text-gold text-7xl sm:text-8xl leading-none mb-4">
                    {s.number}
                  </div>
                  <div className="overline mb-3">Engagement {s.number}</div>
                  <h2 className="font-serif text-3xl sm:text-4xl text-navy leading-tight">
                    {s.title}
                  </h2>
                </div>

                <div className="lg:col-span-8 grid grid-cols-1 sm:grid-cols-2 gap-8 lg:gap-10">
                  <div>
                    <div className="overline text-[10px] mb-2 text-teal">
                      The Problem
                    </div>
                    <p className="font-sans text-sm text-navy/80 leading-relaxed">
                      {s.problem}
                    </p>
                  </div>
                  <div>
                    <div className="overline text-[10px] mb-2 text-teal">
                      Operational Impact
                    </div>
                    <p className="font-sans text-sm text-navy/80 leading-relaxed">
                      {s.impact}
                    </p>
                  </div>
                  <div>
                    <div className="overline text-[10px] mb-2 text-teal">
                      Compliance and Financial Risk
                    </div>
                    <p className="font-sans text-sm text-navy/80 leading-relaxed">
                      {s.risk}
                    </p>
                  </div>
                  <div>
                    <div className="overline text-[10px] mb-2 text-gold">
                      How AthenaCrest Helps
                    </div>
                    <p className="font-sans text-sm text-navy/90 leading-relaxed font-medium">
                      {s.solution}
                    </p>
                  </div>
                </div>

                {idx < SERVICES.length - 1 && (
                  <div className="lg:col-span-12 mt-6 editorial-rule" />
                )}
              </article>
            ))}
          </div>
        </div>
      </section>

      <CTABand
        testId="services-cta-band"
        eyebrow="Ready to Begin"
        headline="Tell us where the facility hurts most."
        sub="A 30 minute confidential consultation maps the highest priority operational risk and the engagement that resolves it."
      />
    </div>
  );
}
